import { matchPath } from "react-router-dom";
import type { PageDefinition } from "./types/page-reference.js";

export const standalonePageCodes = { login: "P-001", persona: "P-002", startup: "P-003" } as const;

// Permanent feedback references: retain codes when routes move; never reuse a retired code.
// Routes and feedback references share this registry so additions have an explicit identity.
export const pageDefinitions = {
  dashboard: { code: "P-101", path: "/me" },
  assessment: { code: "P-102", path: "/me/assessment/:pageId" },
  chat: { code: "P-103", path: "/me/chat" },
  passport: { code: "P-104", path: "/me/passport" },
  passportDomain: { code: "P-105", path: "/me/passport/:pageId" },
  stamp: { code: "P-106", path: "/me/stamps/:stampId" },
  skill: { code: "P-107", path: "/me/skills/:skillId" },
  activity: { code: "P-108", path: "/me/activity" },
  onboarding: { code: "P-109", path: "/me/onboarding" },
  profile: { code: "P-110", path: "/me/profile" },
  checklist: { code: "P-201", path: "/manage/checklist" },
  checklistItem: { code: "P-210", path: "/manage/checklist/:itemId" },
  readiness: { code: "P-202", path: "/manage/readiness" },
  planning: { code: "P-203", path: "/manage/planning" },
  insights: { code: "P-204", path: "/manage/insights" },
  catalogue: { code: "P-205", path: "/manage/catalogue" },
  organisation: { code: "P-206", path: "/manage/organisation" },
  users: { code: "P-207", path: "/manage/users" },
  school: { code: "P-208", path: "/manage/school" },
  theme: { code: "P-209", path: "/manage/theme" },
  managerChat: { code: "P-214", path: "/manage/chat" },
  managerDashboard: { code: "P-211", path: "/manage" },
  managerCourseRanking: { code: "P-212", path: "/manage/courses" },
  schoolSetup: { code: "P-213", path: "/manage/school/setup" },
  schoolDocuments: { code: "P-216", path: "/manage/documents" },
  mentorMentees: { code: "P-215", path: "/manage/mentees" },
  adminDashboard: { code: "P-301", path: "/admin" },
  adminOrganisations: { code: "P-302", path: "/admin/organisations" },
  adminOrganisation: { code: "P-303", path: "/admin/organisations/:organisationId" },
  adminTheme: { code: "P-304", path: "/admin/organisations/:organisationId/theme" },
  adminUsers: { code: "P-305", path: "/admin/users" },
  adminUser: { code: "P-306", path: "/admin/users/:userId" },
  adminConfiguration: { code: "P-307", path: "/admin/configuration" },
  adminActivity: { code: "P-308", path: "/admin/activity" },
  adminSituationCards: { code: "P-309", path: "/admin/situation-cards" },
  adminSituationCard: { code: "P-310", path: "/admin/situation-cards/:cardId" },
  // P-311 was the situation-card version history screen and stays retired.
} as const satisfies Record<string, PageDefinition>;

function recordReference(value: string): string {
  try {
    return encodeURIComponent(decodeURIComponent(value));
  } catch {
    return encodeURIComponent(value);
  }
}

export function pageReferenceForPath(pathname: string): string | null {
  for (const page of Object.values(pageDefinitions)) {
    const match = matchPath({ path: page.path, end: true }, pathname);
    if (!match) continue;
    const records = Object.values(match.params).filter((value): value is string => value !== undefined);
    return [page.code, ...records.map(recordReference)].join("/");
  }
  return null;
}
